import React, { useState } from 'react';
import { Mail, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import useDemoTracker from '../../hooks/useDemoTracker';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Contact lead form — final CTA after the demo runs through.
 * Posts {email, name, organisation, message, source: 'demo'} to the backend.
 */
export default function DemoContactForm({ onRestart }) {
  const [form, setForm] = useState({ name: '', email: '', organisation: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');
  const { track } = useDemoTracker();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || form.email.length < 5 || !form.email.includes('@')) {
      setErrorMsg('Email invalide.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/contact-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'demo' }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Échec d’envoi');
      }
      track('demo_submitted');
      setStatus('sent');
    } catch (err) {
      setErrorMsg(err.message);
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 px-6 py-6 max-w-md mx-auto text-center" data-testid="demo-contact-sent">
        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
        <h3 className="font-bold text-emerald-100 text-lg">Demande envoyée !</h3>
        <p className="text-sm text-slate-300 mt-2">
          Merci {form.name || 'à vous'} — nous revenons par email à <strong className="text-emerald-200">{form.email}</strong> sous 24 h ouvrées.
        </p>
        <button
          type="button"
          onClick={onRestart}
          className="mt-4 text-xs text-slate-400 hover:text-slate-200 underline underline-offset-2"
          data-testid="demo-contact-restart"
        >
          Revoir la démo depuis le début
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-ink-600 bg-card/95 backdrop-blur-md px-6 py-5 max-w-md mx-auto space-y-3"
      data-testid="demo-contact-form"
    >
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-5 h-5 text-accent" />
        <h3 className="font-bold text-slate-100">Discutons de votre cohorte</h3>
      </div>
      <p className="text-xs text-slate-400 leading-relaxed">
        Laissez-nous votre email — nous vous proposerons une licence adaptée à votre établissement.
      </p>

      <Field label="Nom complet" type="text" value={form.name} onChange={(v) => set('name', v)} testid="demo-input-name" />
      <Field label="Email pro *" type="email" value={form.email} onChange={(v) => set('email', v)} testid="demo-input-email" required />
      <Field label="Organisation (école, CFA…)" type="text" value={form.organisation} onChange={(v) => set('organisation', v)} testid="demo-input-org" />
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Message (optionnel)</label>
        <textarea
          value={form.message}
          onChange={(e) => set('message', e.target.value)}
          rows={2}
          maxLength={500}
          className="w-full bg-background border border-ink-600 rounded-md px-3 py-2 text-sm text-foreground placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
          placeholder="Combien d’apprenants ? Quels objectifs pédagogiques ?"
          data-testid="demo-input-message"
        />
      </div>

      {status === 'error' && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-danger/10 border border-danger/40 text-xs text-danger" data-testid="demo-contact-error">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'sending' || !form.email}
        className="w-full bg-accent hover:bg-accent-hover text-white py-2.5 rounded-md text-sm font-bold uppercase tracking-wider transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="demo-contact-submit"
      >
        {status === 'sending'
          ? (<><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</>)
          : (<><Send className="w-4 h-4" /> Envoyer ma demande</>)}
      </button>

      <button
        type="button"
        onClick={onRestart}
        className="w-full text-[11px] text-slate-500 hover:text-slate-300 underline underline-offset-2"
        data-testid="demo-contact-restart-form"
      >
        Recommencer la démo
      </button>
    </form>
  );
}

function Field({ label, type, value, onChange, testid, required }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-background border border-ink-600 rounded-md px-3 py-2 text-sm text-foreground placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent"
        data-testid={testid}
      />
    </div>
  );
}
